import React, { FunctionComponent, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { Nanoseconds, Timer } from '@spotify-internal/semantic-metrics';

import { setup, startLiveEngage } from 'lib/client/liveEngage';
import { getLiveEngageLocale } from 'lib/client/liveEngage/utils';
import { DEFAULT_LOCALE } from 'utils/i18n/localeConfig';
import { isSupportedMessagingLocale } from 'utils/market/market';
import { sendTimerMetric, sendCounterMetric } from 'utils/metrics';
import { MESSAGING_ENABLED } from 'utils/config';
import { Vertical } from 'utils/vertical';
import { useServerSideConstants } from 'providers/ServerSideConstantsProvider';
import { useCustomer } from 'utils/user';

import { DEFAULT_STATUS, DISABLED_STATUS, MessagingAvailabilityReason, MessagingType } from './types';
import { MessagingContext } from './MessagingContext';

interface Props {
  locale: string;
  marketCode: string;
  children: React.ReactNode;
}

const sendLiveEngageMetrics = ({
  setupTime,
  locale,
  resolution,
  pathname,
  vertical,
}: {
  setupTime: Nanoseconds<number>;
  locale: string;
  resolution: MessagingAvailabilityReason;
  pathname: string;
  vertical: Vertical;
}) => {
  sendTimerMetric('live-engage-setup-time', setupTime, {
    locale,
    resolution,
    pathname,
    vertical,
  });
  sendCounterMetric('live-engage-setup-count', {
    locale,
    resolution,
    pathname,
    vertical,
  });
};

export const LivePersonMessagingProvider: FunctionComponent<Props> = ({ locale, marketCode, children }) => {
  const [status, setStatus] = useState(MESSAGING_ENABLED ? DEFAULT_STATUS : DISABLED_STATUS);
  const { user, isFetching } = useCustomer();

  const {
    verticalConfig: {
      verticalName,
      locales: { messagingLocales },
    },
  } = useServerSideConstants();

  const localeToUse = isSupportedMessagingLocale(locale, messagingLocales) ? locale : DEFAULT_LOCALE;
  const liveEngageLocale = getLiveEngageLocale(localeToUse);

  const { pathname } = useRouter();

  const valueProp: MessagingType = {
    status,
    startMessaging: status.isAvailable
      ? () => {
          startLiveEngage(marketCode, liveEngageLocale, verticalName);
        }
      : undefined,
  };

  useEffect(() => {
    if (!MESSAGING_ENABLED || isFetching) return;
    sendCounterMetric('live-engage-setup-started', { locale: liveEngageLocale, pathname, vertical: verticalName });

    const getLiveEngagestatus = async () => {
      const timer = Timer.start();
      const messagingStatus = await setup(liveEngageLocale, user?.id);
      sendLiveEngageMetrics({
        setupTime: timer.end().nanoseconds,
        locale: liveEngageLocale,
        resolution: messagingStatus.reason,
        pathname,
        vertical: verticalName,
      });
      setStatus(messagingStatus);
    };
    getLiveEngagestatus();
  }, [liveEngageLocale, pathname, verticalName, isFetching, user]);

  return <MessagingContext.Provider value={valueProp}>{children}</MessagingContext.Provider>;
};
